/**
 * AllOwnersDeceased — RSM wizard for SDB "All Owners Deceased" workflow.
 *
 * Wave 2 (locked 2026-04-27 "go all defaults" — decision #8). Captures the
 * canonical PCB joint-inventory procedure verbatim from
 * `Docs/Legacy Procedures/Account Maintenance/SDB – All Owners Deceased.pdf`:
 *
 *   1. Family member presents → officer/supervisor enters box WITH them
 *   2. If will found in box: take COPY, give ORIGINAL to family. Copy stays.
 *   3. Inventory list → COPY to family, ORIGINAL stays in box
 *   4. NO other items handed over at this point
 *   5. No family + rent past-due → standard drill procedures (escheats from there)
 *
 * Backend: Wave 3 will add a dedicated SdbAllDeceasedCases table; for now
 * fires a structured SessionEvent to preserve audit trail and queues the
 * Escheatment handoff via the existing flow.
 */

import { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { useApiClient } from '@/contexts/ApiClientContext';
import { useAuth } from '@/contexts/AuthContext';
import { Button, Pill } from '@/components/primitives';
import { useSdbDetail } from '../hooks/useSdbDetail';
import '../rsm.css';

type Step = 'intake' | 'inventory' | 'will-handling' | 'handoff' | 'submitted';

interface FormState {
  decedentNames: string;
  dateOfFirstDeath: string;
  dateOfLastDeath: string;
  familyMemberPresent: boolean;
  familyMemberName: string;
  officerWitnessUpn: string;
  supervisorWitnessUpn: string;
  inventoryDescription: string;
  willFoundInBox: boolean;
  willCopyRetained: boolean;
  willOriginalToFamily: boolean;
  inventoryCopyToFamily: boolean;
  noFamilyEscheatPath: boolean;
  notes: string;
}

const INITIAL: FormState = {
  decedentNames: '',
  dateOfFirstDeath: '',
  dateOfLastDeath: new Date().toISOString().slice(0, 10),
  familyMemberPresent: true,
  familyMemberName: '',
  officerWitnessUpn: '',
  supervisorWitnessUpn: '',
  inventoryDescription: '',
  willFoundInBox: false,
  willCopyRetained: false,
  willOriginalToFamily: false,
  inventoryCopyToFamily: true,
  noFamilyEscheatPath: false,
  notes: '',
};

export function AllOwnersDeceased(): JSX.Element {
  const { acctId } = useParams<{ acctId: string }>();
  const navigate = useNavigate();
  const apiClient = useApiClient();
  const { user } = useAuth();
  const { data: box } = useSdbDetail(acctId);

  const [step, setStep] = useState<Step>('intake');
  const [form, setForm] = useState<FormState>({ ...INITIAL, officerWitnessUpn: user?.upn ?? '' });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const update = <K extends keyof FormState>(k: K, v: FormState[K]): void => {
    setForm((prev) => ({ ...prev, [k]: v }));
  };

  const submit = async (): Promise<void> => {
    if (!acctId) return;
    setSubmitting(true);
    setError(null);
    try {
      await apiClient.retail.events.write({
        eventType: 'sdb-all-owners-deceased',
        eventCategory: 'compliance',
        severity: 'warn',
        actor: user?.upn ?? 'unknown',
        payloadJson: JSON.stringify({
          acctId,
          boxNumber: box?.boxNumber ?? null,
          branchId: box?.branchId ?? null,
          ...form,
        }),
      });
      setStep('submitted');
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : 'Submit failed.';
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const goEscheat = (): void => {
    if (acctId) navigate(`/rsm/box/${acctId}/escheat`);
  };

  const Header = (
    <header style={{ marginBottom: 'var(--sp-5)' }}>
      <h2 style={{ margin: 0, fontSize: 18, fontWeight: 600 }}>SDB — All Owners Deceased</h2>
      <p style={{ margin: 'var(--sp-2) 0 0 0', fontSize: 12, color: 'var(--ink-700)' }}>
        Box: <strong>{box?.boxNumber ?? acctId}</strong>
        {box?.primaryCustomerName && <> ({box.primaryCustomerName})</>}
      </p>
    </header>
  );

  if (step === 'submitted') {
    return (
      <div style={{ padding: 'var(--sp-5)' }}>
        {Header}
        <div style={successStyle}>
          <strong>SDB All-Deceased intake recorded.</strong> SessionEvent <code>sdb-all-owners-deceased</code> fired.
          <br />Next step depends on the outcome captured in this intake — pick the path that matches:
        </div>
        <div style={{ marginTop: 'var(--sp-4)', display: 'flex', flexDirection: 'column', gap: 'var(--sp-3)' }}>
          {/* Path 1 — Estate-claim: Personal Rep claims contents, account funds + box go to estate. */}
          <Button
            variant="primary"
            onClick={() => {
              // Deep-link to RAO Estate intent with decedent CIF + Personal Rep prefilled.
              const qp = new URLSearchParams({
                intent: 'estate',
                decedentCif: box?.primaryCifId ?? '',
                decedentName: form.decedentNames || (box?.primaryCustomerName ?? ''),
                fromSdb: acctId ?? '',
              });
              navigate(`/rao?${qp.toString()}`);
            }}
            disabled={!form.familyMemberPresent}
          >
            Open Estate-claim workflow (RAO)
          </Button>
          {!form.familyMemberPresent && (
            <p style={{ fontSize: 11, color: 'var(--ink-700)', margin: 0 }}>
              Estate-claim requires a Personal Rep. No family present at this intake.
            </p>
          )}

          {/* Path 2 — Family member claims contents directly via standard close-out wizard. */}
          <Button
            variant="ghost"
            onClick={() => {
              if (acctId) navigate(`/rsm/box/${acctId}/close`);
            }}
            disabled={!form.familyMemberPresent}
          >
            Family-claim — open standard Close-Out wizard
          </Button>

          {/* Path 3 — Escheatment: no family + rent past-due → MO RSMo 447.539.9 path. */}
          <Button variant="ghost" onClick={goEscheat}>
            No family + rent past-due — Escheatment handoff
          </Button>

          <Button variant="ghost" onClick={() => navigate('/rsm')}>Back to RSM</Button>
        </div>
      </div>
    );
  }

  const StepBadge = ({ active, n, label }: { active: boolean; n: number; label: string }) => (
    <div style={{
      display: 'flex',
      alignItems: 'center',
      gap: 'var(--sp-2)',
      padding: 'var(--sp-2) var(--sp-3)',
      borderRadius: 'var(--r-sm)',
      background: active ? 'var(--brand-50, #e6f0fa)' : 'transparent',
      color: active ? 'var(--brand-900, #1a406a)' : 'var(--ink-700)',
      fontWeight: active ? 600 : 400,
      fontSize: 12,
    }}>
      <span style={{
        width: 20, height: 20, borderRadius: 999,
        background: active ? 'var(--brand-700, #2968ad)' : 'var(--ink-300)',
        color: 'var(--on-brand)', display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        fontSize: 11, fontWeight: 700,
      }}>{n}</span>
      <span>{label}</span>
    </div>
  );

  return (
    <div style={{ padding: 'var(--sp-5)' }}>
      {Header}

      <div style={{ display: 'flex', gap: 'var(--sp-3)', marginBottom: 'var(--sp-5)', flexWrap: 'wrap' }}>
        <StepBadge active={step === 'intake'}        n={1} label="Intake" />
        <StepBadge active={step === 'inventory'}     n={2} label="Inventory" />
        <StepBadge active={step === 'will-handling'} n={3} label="Will handling" />
        <StepBadge active={step === 'handoff'}       n={4} label="Handoff" />
      </div>

      {step === 'intake' && (
        <div style={{ display: 'grid', gap: 'var(--sp-4)', maxWidth: 720 }}>
          <Field label="Decedent names (all owners)">
            <input type="text" value={form.decedentNames}
                   onChange={(e) => update('decedentNames', e.target.value)} style={inputStyle} />
          </Field>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--sp-4)' }}>
            <Field label="First death (optional)">
              <input type="date" value={form.dateOfFirstDeath}
                     onChange={(e) => update('dateOfFirstDeath', e.target.value)} style={inputStyle} />
            </Field>
            <Field label="Last death">
              <input type="date" value={form.dateOfLastDeath}
                     onChange={(e) => update('dateOfLastDeath', e.target.value)} style={inputStyle} />
            </Field>
          </div>
          <CheckRow label="Family member present at branch" value={form.familyMemberPresent}
                    onChange={(v) => update('familyMemberPresent', v)} />
          {form.familyMemberPresent ? (
            <Field label="Family member name + relationship to decedent">
              <input type="text" value={form.familyMemberName}
                     onChange={(e) => update('familyMemberName', e.target.value)} style={inputStyle} />
            </Field>
          ) : (
            <CheckRow
              label="No family + rent past-due → escheat path"
              helper="If no family comes forward and rent is past-due, follow standard drill procedures (escheats from there)."
              value={form.noFamilyEscheatPath}
              onChange={(v) => update('noFamilyEscheatPath', v)}
            />
          )}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--sp-4)' }}>
            <Field label="Officer witness (UPN)">
              <input type="text" value={form.officerWitnessUpn}
                     onChange={(e) => update('officerWitnessUpn', e.target.value)} style={inputStyle} />
            </Field>
            <Field label="Supervisor witness (UPN)">
              <input type="text" value={form.supervisorWitnessUpn}
                     onChange={(e) => update('supervisorWitnessUpn', e.target.value)} style={inputStyle} />
            </Field>
          </div>
          <div style={{ display: 'flex', gap: 'var(--sp-3)' }}>
            <Button variant="ghost" onClick={() => navigate('/rsm')}>Cancel</Button>
            <Button variant="primary" onClick={() => setStep('inventory')}>Next: Inventory</Button>
          </div>
        </div>
      )}

      {step === 'inventory' && (
        <div style={{ display: 'grid', gap: 'var(--sp-4)', maxWidth: 720 }}>
          <div style={infoNoteStyle}>
            <strong>Per PCB procedure:</strong> Officer/Supervisor enters the box WITH the family member.
            Inventory list — <em>copy to family, original stays in box</em>. <strong>No other items</strong> handed over at this point.
          </div>
          <Field label="Inventory description">
            <textarea value={form.inventoryDescription}
                      onChange={(e) => update('inventoryDescription', e.target.value)}
                      rows={6} style={{ ...inputStyle, resize: 'vertical' }}
                      placeholder="E.g., 3 envelopes (sealed), gold ring (yellow metal, ~10g), passport (decedent), cash $250..." />
          </Field>
          <CheckRow label="Inventory copy provided to family member; original retained in box"
                    value={form.inventoryCopyToFamily}
                    onChange={(v) => update('inventoryCopyToFamily', v)} />
          <div style={{ display: 'flex', gap: 'var(--sp-3)' }}>
            <Button variant="ghost" onClick={() => setStep('intake')}>Back</Button>
            <Button variant="primary"
                    onClick={() => setStep('will-handling')}
                    disabled={!form.inventoryDescription.trim()}>Next: Will handling</Button>
          </div>
        </div>
      )}

      {step === 'will-handling' && (
        <div style={{ display: 'grid', gap: 'var(--sp-4)', maxWidth: 720 }}>
          <CheckRow label="Will found in box" value={form.willFoundInBox}
                    onChange={(v) => update('willFoundInBox', v)} />
          {form.willFoundInBox && (
            <>
              <div style={infoNoteStyle}>
                <strong>Per PCB procedure:</strong> take a <strong>COPY</strong> of the will, give the <strong>ORIGINAL</strong> to family.
                Copy stays in box.
              </div>
              <CheckRow label="Copy of will retained in box" value={form.willCopyRetained}
                        onChange={(v) => update('willCopyRetained', v)} />
              <CheckRow label="Original will handed to family" value={form.willOriginalToFamily}
                        onChange={(v) => update('willOriginalToFamily', v)} />
            </>
          )}
          <div style={{ display: 'flex', gap: 'var(--sp-3)' }}>
            <Button variant="ghost" onClick={() => setStep('inventory')}>Back</Button>
            <Button variant="primary"
                    onClick={() => setStep('handoff')}
                    disabled={form.willFoundInBox && (!form.willCopyRetained || !form.willOriginalToFamily)}>
              Next: Handoff
            </Button>
          </div>
        </div>
      )}

      {step === 'handoff' && (
        <div style={{ display: 'grid', gap: 'var(--sp-4)', maxWidth: 720 }}>
          <Field label="Notes (Estate-claim handoff details, follow-up actions, etc.)">
            <textarea value={form.notes}
                      onChange={(e) => update('notes', e.target.value)}
                      rows={4} style={{ ...inputStyle, resize: 'vertical' }} />
          </Field>
          <div style={infoNoteStyle}>
            On submit, SessionEvent <code>sdb-all-owners-deceased</code> fires. Next steps:
            <ul style={{ margin: 'var(--sp-2) 0 0 var(--sp-5)', paddingLeft: 0 }}>
              <li>Estate-claim path: route to RAO Estate flow (decedent + Personal Rep)</li>
              <li>Rent past-due + no family: open Escheatment handoff (MO RSMo 447.539.9)</li>
              <li>Family-claim path: standard SDB Close-Out wizard</li>
            </ul>
          </div>
          {error && <div style={errorStyle} role="alert">{error}</div>}
          <div style={{ display: 'flex', gap: 'var(--sp-3)' }}>
            <Button variant="ghost" onClick={() => setStep('will-handling')} disabled={submitting}>Back</Button>
            <Button variant="primary" onClick={() => void submit()} disabled={submitting}>
              {submitting ? 'Submitting...' : 'Record All-Deceased intake'}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

function CheckRow({
  label, helper, value, onChange,
}: {
  label: string;
  helper?: string;
  value: boolean;
  onChange: (v: boolean) => void;
}): JSX.Element {
  return (
    <label style={{
      display: 'flex',
      alignItems: 'flex-start',
      gap: 'var(--sp-3)',
      padding: 'var(--sp-2) 0',
      cursor: 'pointer',
    }}>
      <input type="checkbox" checked={value} onChange={(e) => onChange(e.target.checked)} style={{ marginTop: 2 }} />
      <div>
        <div style={{ fontSize: 13, fontWeight: 500 }}>{label}</div>
        {helper && <div style={{ fontSize: 11, color: 'var(--ink-700)', marginTop: 2 }}>{helper}</div>}
      </div>
    </label>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }): JSX.Element {
  return (
    <label style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-1)' }}>
      <span style={{ fontSize: 11, textTransform: 'uppercase', color: 'var(--ink-500)' }}>{label}</span>
      {children}
    </label>
  );
}

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: 'var(--sp-3)',
  border: '1px solid var(--ink-300)',
  borderRadius: 'var(--r-md)',
  fontSize: 13,
};

const errorStyle: React.CSSProperties = {
  padding: 'var(--sp-3)',
  background: 'var(--err-bg)',
  color: 'var(--err-text)',
  borderRadius: 'var(--r-md)',
  fontSize: 12,
};

const infoNoteStyle: React.CSSProperties = {
  padding: 'var(--sp-3)',
  background: 'var(--info-bg)',
  color: 'var(--info-text)',
  borderRadius: 'var(--r-md)',
  fontSize: 12,
};

const successStyle: React.CSSProperties = {
  padding: 'var(--sp-4)',
  background: 'var(--ok-bg, #e8f5e9)',
  color: 'var(--ok-text, #1b5e20)',
  borderRadius: 'var(--r-md)',
  fontSize: 13,
};
